import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Project, ProjectCreate, ProjectUpdate } from '../types/project';
import { userApi } from '../api/userApi';
import { projectUserApi } from '../api/projectUserApi';
import { User } from '../types/models';
import { isSystemAdminUser } from '../utils/permissions';

interface ProjectFormData {
  code: string;
  name: string;
  description?: string;
  user_ids?: number[];
}

interface ProjectFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProjectCreate | ProjectUpdate) => Promise<void>;
  project?: Project;
}

export const ProjectFormDialog: React.FC<ProjectFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  project,
}) => {
  const [apiError, setApiError] = React.useState<string | null>(null);
  const [users, setUsers] = React.useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = React.useState<number[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormData>({
    defaultValues: {
      code: project?.code || '',
      name: project?.name || '',
      description: project?.description || '',
    },
  });

  // Fetch users when dialog opens
  React.useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const usersData = await userApi.getUsers();
        const activeUsers = usersData.filter((user) => user.is_active);
        setUsers(activeUsers);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Fetch project's associated users
  React.useEffect(() => {
    const fetchProjectUsers = async () => {
      if (project?.id) {
        try {
          const projectUserIds = await projectUserApi.getProjectUsers(project.id);
          
          // Filter out system admin users from the selected users
          const filteredUserIds = projectUserIds.filter(userId => {
            const user = users.find(u => u.id === userId);
            return user && !isSystemAdminUser(user);
          });
          
          setSelectedUsers(filteredUserIds);
        } catch (error) {
          console.error('Failed to fetch project users:', error);
          setSelectedUsers([]);
        }
      }
    };

    if (open && project) {
      fetchProjectUsers();
    } else {
      setSelectedUsers([]);
    }
  }, [open, project, users]);

  React.useEffect(() => {
    if (open) {
      setApiError(null);
      // Only reset the form when the dialog opens, not when selectedUsers changes
      if (!project) {
        // For new projects, reset everything
        reset({
          code: '',
          name: '',
          description: '',
          user_ids: [],
        });
      } else {
        // For existing projects, set the initial values
        reset({
          code: project.code || '',
          name: project.name || '',
          description: project.description || '',
          user_ids: selectedUsers,
        });
      }
    }
  }, [open, project, reset]);

  // Separate effect to update just the user_ids field when selectedUsers changes
  React.useEffect(() => {
    if (open) {
      // Only update the user_ids field without resetting the entire form
      setValue('user_ids', selectedUsers);
    }
  }, [selectedUsers, setValue, open]);

  const handleFormSubmit = async (data: ProjectFormData) => {
    try {
      setApiError(null);
      await onSubmit(data);
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'An error occurred while saving the project';
      setApiError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{project ? 'Edit Event' : 'Create Event'}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: 'Code is required',
                pattern: {
                  value: /^[A-Za-z0-9-_]+$/,
                  message: 'Code can only contain letters, numbers, hyphens, and underscores',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Code"
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                />
              )}
            />

            {/* User Selection */}
            <FormControl fullWidth>
              <InputLabel id="user-select-label">Authorized Users</InputLabel>
              <Controller
                name="user_ids"
                control={control}
                defaultValue={selectedUsers}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="user-select-label"
                    label="Authorized Users"
                    multiple
                    value={selectedUsers}
                    onChange={(e) => {
                      const value = e.target.value as number[];
                      setSelectedUsers(value);
                      setValue('user_ids', value); // Directly set the form value
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((userId) => {
                          const user = users.find(u => u.id === userId);
                          // Skip rendering system admin users in chips
                          if (user && isSystemAdminUser(user)) {
                            return null;
                          }
                          return (
                            <Chip key={userId} label={user?.full_name || `User ${userId}`} />
                          );
                        }).filter(Boolean)}
                      </Box>
                    )}
                    disabled={isLoadingUsers}
                  >
                    {users
                      .filter(user => !isSystemAdminUser(user))
                      .map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                          <ListItemText primary={user.full_name} secondary={user.email} />
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || isLoadingUsers}
          >
            {project ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
